<template>
  <PageLoader class="mb-5 mt-5" v-if="isLoading"></PageLoader>
  <div v-if="isLoaded" v-show="!isLoading">

    <!-- ════ INTRO MODAL — brief "machines in motion" beat on load, then it fades and the film plays ════ -->
    <div class="ty-intro" id="ty-intro" role="dialog" aria-modal="true" aria-label="The machines are in motion">
      <div class="ty-intro__panel">
        <div class="gl-stage ty-intro__gears" data-gears-loader aria-hidden="true">
          <div class="gl-bloom" data-gl="bloom"></div>
          <canvas class="gl-gl" data-gl="gl"></canvas>
          <canvas class="gl-fx" data-gl="fx"></canvas>
          <img width="250" data-gl="still"
            src="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/images/gears-still.png"
            alt="">
          <video class="gl-video" data-gl="video"
            src="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/videos/moving-gears.webm"
            preload="auto" playsinline muted loop></video>
        </div>
        <h1>The Machines<br>Are In Motion</h1>
        <p>Your receipt is making its way through the factory now. Stand by while we prepare what comes next.</p>
      </div>
    </div>

    <!-- ════ THANK YOU STAGE — video plays once, holds, blurs, then the card reveals ════ -->
    <section class="ty-stage" id="ty-stage" data-screen-label="Thank You">
      <video class="ty-media ty-video" id="ty-video" muted playsinline preload="auto"
        poster="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/images/ty-firstframe-desktop.jpg">
        <source
          src="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/videos/thankyou-mobile.mp4"
          type="video/mp4" media="(max-width:768px)">
        <source
          src="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/videos/thankyou-desktop.mp4"
          type="video/mp4">
      </video>
      <picture class="ty-media ty-firstframe" id="ty-firstframe" aria-hidden="true">
        <source media="(max-width:768px)"
          srcset="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/images/ty-firstframe-mobile.jpg">
        <img
          src="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/images/ty-firstframe-desktop.jpg"
          alt="" decoding="async">
      </picture>
      <picture class="ty-media ty-blur" id="ty-blur" aria-hidden="true">
        <source media="(max-width:768px)"
          srcset="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/images/ty-blur-mobile.jpg">
        <img src="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/images/ty-blur-desktop.jpg"
          alt="" decoding="async">
      </picture>
      <div class="ty-accents" aria-hidden="true"></div>
      <div class="ty-card" id="ty-card">
        <span class="card-glow" aria-hidden="true"></span>
        <img class="ty-ticket"
          src="https://snippcheck.blob.core.windows.net/files/WKKC_Golden_Ticket_2008937/images/ticket-search-begun.png"
          alt="Your Search Has Begun" decoding="async">
        <p class="eyebrow">Splendiferous!</p>
        <h2>We'll Handle The Magic From Here</h2>
        <p class="ty-card__copy">The Oompa Loompas are reviewing your submission now. Keep an eye on your inbox,
          we'll let you know once your qualifying purchase has been approved.</p>
        <div class="ty-actions">
          <router-link to="/Upload" class="btn-gold">Upload Another Receipt</router-link>
          <router-link to="/" class="btn-outline-light">Return To The Factory</router-link>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import PageLoader from '@/components/UI/PageLoader.vue';

export default {
  name: 'ThanksPage',
  components: { PageLoader },
  data() {
    return {
      isLoading: true,
      isLoaded: false,
    };
  },
  mounted() {
    // ✅ Dynamically import gears-loader.js after the component mounts so the
    // DOM elements it targets (#gears-loader, [data-gl]) are already in the page
    import('../../../public/assets/js/gears-loader.js');

    const setupCardGlow = () => {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const card = document.querySelector('.ty-card');
      if (!card) return;

      let glow = 0;
      let targetGlow = 0;
      let angle = 0;
      let targetAngle = 0;
      let raf = null;
      const THRESH = 0.60;

      const onMove = (e) => {
        const r = card.getBoundingClientRect();
        const cx = r.width / 2;
        const cy = r.height / 2;
        const dx = (e.clientX - r.left) - cx;
        const dy = (e.clientY - r.top) - cy;
        const kx = dx !== 0 ? cx / Math.abs(dx) : Infinity;
        const ky = dy !== 0 ? cy / Math.abs(dy) : Infinity;
        const edge = Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
        const g = edge < THRESH ? 0 : (edge - THRESH) / (1 - THRESH);
        let deg = Math.atan2(dy, dx) * 180 / Math.PI + 90;
        if (deg < 0) deg += 360;
        const nearestCorner = Math.round((deg - 45) / 90) * 90 + 45;
        const cdist = Math.abs(((deg - nearestCorner + 540) % 360) - 180);
        const corner = Math.max(0, 1 - cdist / 45);
        targetGlow = Math.min(g * (0.82 + 0.34 * corner), 1);
        targetAngle = deg;
        start();
      };

      const onLeave = () => {
        targetGlow = 0;
        start();
      };

      const start = () => {
        if (!raf) raf = requestAnimationFrame(step);
      };

      const step = () => {
        glow += (targetGlow - glow) * 0.10;
        const d = ((targetAngle - angle + 540) % 360) - 180;
        angle = (angle + d * 0.14 + 360) % 360;
        card.style.setProperty('--hglow', glow.toFixed(3));
        card.style.setProperty('--hangle', angle.toFixed(1) + 'deg');
        if (Math.abs(targetGlow - glow) > 0.002 || Math.abs(d) > 0.4) {
          raf = requestAnimationFrame(step);
        } else {
          raf = null;
        }
      };

      card.addEventListener('pointermove', onMove);
      card.addEventListener('pointerleave', onLeave);
    };

    this.isLoaded = true;
    // ✅ Wait for the loader to clear, then let Vue flush the DOM before starting the sequence
    setTimeout(() => {
      this.isLoading = false;
      this.$nextTick(() => {
        setupCardGlow();
        this.initThankyouSequence();
      });
    }, 1500);

   
  },
  methods: {
    
    // ✅ Thank-you sequence: intro modal holds a beat, fades out, then the film plays once,
    // holds on its last frame, the background blurs, and the message card slides up.
    initThankyouSequence() {
      var stage = document.querySelector('.ty-stage');
      if (!stage) return;

      var video = stage.querySelector('.ty-video');
      var blur = stage.querySelector('.ty-blur');
      var card = stage.querySelector('.ty-card');
      var firstframe = document.getElementById('ty-firstframe');
      var intro = document.getElementById('ty-intro');
      var staticMode = /[?&]static=1/.test(window.location.search);
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function reveal() {
        blur.classList.add('is-in');
        setTimeout(function () { card.classList.add('is-in'); }, 520);
      }

      function endNow() {
        if (video) { try { video.pause(); } catch (e) { /* pause() may throw if video is not yet loaded — safe to ignore */ } }
        blur.classList.add('is-in');
        card.classList.add('is-in');
      }

      function startVideo() {
        if (reduce || !video) { endNow(); return; }
        video.addEventListener('ended', function () { setTimeout(reveal, 750); });
        video.addEventListener('error', endNow);
        var p = video.play();
        if (p && p.catch) p.catch(function () { endNow(); });
      }

      var done = false, timer = null;

      function dismissIntro() {
        if (done) return; done = true;
        if (timer) clearTimeout(timer);
        document.body.style.overflow = '';
        if (firstframe) firstframe.classList.add('is-gone');
        if (staticMode) { endNow(); }    // static fallback: no video, straight to end card
        else { startVideo(); } // get the film running underneath
        setTimeout(function () {          // then fade the opaque modal to reveal it
          if (intro) { intro.classList.remove('is-in'); intro.setAttribute('aria-hidden', 'true'); }
        }, 260);
      }

      // Always show the intro transition first. Under reduced motion it appears without
      // fade and the gears render as a still frame (see gears-loader.js), then it dismisses
      // straight to the end card (startVideo() falls back to endNow() when reduce is set).
      if (intro) {
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { intro.classList.add('is-in'); });
        });
        intro.addEventListener('click', dismissIntro);  // tap to skip
        timer = setTimeout(dismissIntro, 4600);         // hold a beat then auto-dismiss
      } else {
        if (staticMode) { endNow(); } else { startVideo(); }
      }
    },
  },
};
</script>

<style scoped>
.ty-stage {
  position: relative;
  width: 100%;
  overflow: hidden;
}
</style>